import { PoemHighlight } from '@/types/highlights';

/**
 * Literary categories and associated terms for identifying primary topics in notes
 */
interface LiteraryCategory {
  category: string;
  terms: string[];
}

/**
 * Common literary categories with associated terms
 * Used to identify the category a note might belong to
 */
const LITERARY_CATEGORIES: LiteraryCategory[] = [
  {
    category: 'Imagery',
    terms: ['image', 'visual', 'sensory', 'sight', 'picture', 'scene', 'landscape', 'portrays', 'depicts']
  },
  {
    category: 'Metaphor',
    terms: ['metaphor', 'comparison', 'represents', 'symbolizes', 'likens', 'compares', 'equates']
  },
  {
    category: 'Symbolism',
    terms: ['symbol', 'represents', 'signifies', 'embodies', 'stands for', 'symbolic']
  },
  {
    category: 'Personification',
    terms: ['personification', 'personifies', 'humanizes', 'attribute', 'human quality']
  },
  {
    category: 'Alliteration',
    terms: ['alliteration', 'repetition of', 'repeating', 'repeated', 'consonant sound']
  },
  {
    category: 'Tone',
    terms: ['tone', 'mood', 'atmosphere', 'feeling', 'conveys', 'emotional', 'sentiment']
  },
  {
    category: 'Theme',
    terms: ['theme', 'central idea', 'motif', 'subject', 'concept', 'explores', 'addresses']
  },
  {
    category: 'Rhyme',
    terms: ['rhyme', 'rhythm', 'rhyming', 'sound pattern', 'musical']
  },
  {
    category: 'Contrast',
    terms: ['contrast', 'juxtaposition', 'opposing', 'opposition', 'conflicting', 'difference']
  },
  {
    category: 'Repetition',
    terms: ['repetition', 'repeated', 'recurrence', 'echo', 'reinforces', 'emphasizes']
  },
  {
    category: 'Allusion',
    terms: ['allusion', 'reference', 'refers to', 'biblical', 'historical', 'literary']
  }
];

/**
 * Words to be excluded from key term extraction
 * Common articles, prepositions, and generic descriptive words
 */
const STOPWORDS = new Set([
  'a', 'an', 'the', 'and', 'but', 'or', 'for', 'nor', 'on', 'at', 'to', 'from', 'by',
  'about', 'in', 'is', 'are', 'was', 'were', 'be', 'been', 'being',
  'have', 'has', 'had', 'do', 'does', 'did', 'will', 'would', 'shall', 'should',
  'can', 'could', 'may', 'might', 'must', 'this', 'that', 'these', 'those',
  'there', 'here', 'what', 'which', 'who', 'whom', 'whose', 'when', 'where', 'why', 'how',
  'highlight', 'note', 'element', 'technique', 'device', 'example', 'use', 'uses', 'using'
]);

/**
 * Generic labels to avoid
 */
const GENERIC_LABELS = new Set([
  'highlight', 'note', 'important', 'literary device', 'literary technique', 
  'poetic device', 'style element', 'element', 'device', 'technique'
]);

/**
 * Generates a fallback label when a highlight is missing a display_label
 * or when the existing label is too generic
 * 
 * @param highlight The highlight to generate a label for
 * @returns A content-driven label derived from the highlight's note
 */
export function generateFallbackLabel(highlight: PoemHighlight): string {
  // If the highlight already has a non-generic display label, use it
  if (highlight.display_label && 
      !isGenericLabel(highlight.display_label) &&
      highlight.display_label.length > 3) {
    return highlight.display_label;
  }
  
  // No usable display label, extract one from the note
  if (!highlight.note || highlight.note.trim() === '') {
    // No note content to work with, fall back to more specific generic label
    return getDefaultLabel(highlight);
  }
  
  // Try different strategies in order of priority
  
  // 1. First try to extract label from colon format (Category: Details)
  const colonLabel = extractCategoryFromColon(highlight.note);
  if (colonLabel && !isGenericLabel(colonLabel)) {
    return colonLabel;
  }
  
  // 2. Try to identify literary device category from note content
  const categoryLabel = identifyLiteraryCategory(highlight.note);
  if (categoryLabel) {
    // Make the category label more specific by adding a descriptor
    const specificLabel = makeSpecificCategoryLabel(categoryLabel, highlight.note, highlight.text);
    if (specificLabel) {
      return specificLabel;
    }
    return categoryLabel;
  }
  
  // 3. Try to extract key phrases using NLP-inspired approach
  const keyPhrase = extractKeyPhrase(highlight.note);
  if (keyPhrase) {
    return keyPhrase;
  }
  
  // 4. If all else fails, use a formatted version of the first few words
  return formatFirstWords(highlight.note);
}

/**
 * Determines if a label is generic and should be replaced
 * @param label The label to check
 * @returns True if the label is generic
 */
export function isGenericLabel(label: string): boolean {
  if (!label) return true;
  
  // Normalize for comparison
  const normalizedLabel = label.toLowerCase().trim();
  
  // Check against our list of generic labels
  if (GENERIC_LABELS.has(normalizedLabel)) {
    return true;
  }
  
  // Check for single word generic labels
  if (normalizedLabel.split(/\s+/).length === 1 && 
      ['highlight', 'note', 'important', 'element', 'technique', 'device'].includes(normalizedLabel)) {
    return true;
  }
  
  // Very short labels are likely too generic
  if (normalizedLabel.length < 4) {
    return true;
  }
  
  return false;
}

/**
 * Extracts a category from note text in the format "Category: Details"
 * @param note The note text
 * @returns The extracted category or null if not found
 */
function extractCategoryFromColon(note: string): string | null {
  if (note.includes(':')) {
    const parts = note.split(':');
    const category = parts[0].trim();
    
    // Make sure the category is reasonable in length and not just a single character
    if (category.length > 2 && category.length <= 30) {
      return category;
    }
  }
  return null;
}

/**
 * Identifies which literary category a note most closely matches
 * @param note The note text
 * @returns The identified category or null if none found
 */
function identifyLiteraryCategory(note: string): string | null {
  const lowerNote = note.toLowerCase();
  
  // Check each category and its terms
  let bestCategory = null;
  let highestScore = 0;
  
  for (const category of LITERARY_CATEGORIES) {
    let score = 0;
    
    // If the category name itself appears, that's a strong signal
    if (lowerNote.includes(category.category.toLowerCase())) {
      score += 5;
    }
    
    // Check for associated terms
    for (const term of category.terms) {
      if (lowerNote.includes(term.toLowerCase())) {
        score += 1;
      }
    }
    
    if (score > highestScore) {
      highestScore = score;
      bestCategory = category.category;
    }
  }
  
  // Only return if we have a reasonably confident match
  return highestScore >= 1 ? bestCategory : null;
}

/**
 * Makes a generic category label more specific by adding context
 * @param category The literary category (e.g., "Metaphor")
 * @param note The full note text
 * @param highlightText The highlighted text from the poem
 * @returns A more specific label (e.g., "Ocean Metaphor") or null if unable to specify
 */
function makeSpecificCategoryLabel(category: string, note: string, highlightText: string): string | null {
  // Try to find specific nouns or key terms that could modify the category
  
  // First, extract potential subjects from the highlight text itself
  const highlightWords = highlightText.split(/\s+/)
    .filter(word => word.length > 3)
    .filter(word => !STOPWORDS.has(word.toLowerCase()));
  
  // If we find meaningful words in the highlight, use one with the category
  if (highlightWords.length > 0) {
    const significantWord = highlightWords[0];
    // Capitalize first letter
    const formattedWord = significantWord.charAt(0).toUpperCase() + significantWord.slice(1);
    return `${formattedWord} ${category}`;
  }
  
  // If we can't extract from highlight text, try the note
  // Look for nouns after "of" or possessives, which often indicate the subject
  const ofMatches = note.match(/of\s+(\w+)/i);
  if (ofMatches && ofMatches[1] && !STOPWORDS.has(ofMatches[1].toLowerCase())) {
    const subject = ofMatches[1];
    const formattedSubject = subject.charAt(0).toUpperCase() + subject.slice(1);
    return `${formattedSubject} ${category}`;
  }
  
  // Try to extract descriptive adjectives from the note
  const noteWords = note.split(/\s+/)
    .filter(word => word.length > 3)
    .filter(word => !STOPWORDS.has(word.toLowerCase()));
  
  // Find descriptive words that aren't in the category terms
  const categoryTerms = new Set(LITERARY_CATEGORIES.find(c => c.category === category)?.terms || []);
  const descriptiveWords = noteWords.filter(word => !categoryTerms.has(word.toLowerCase()));
  
  if (descriptiveWords.length > 0) {
    const descriptor = descriptiveWords[0];
    const formattedDescriptor = descriptor.charAt(0).toUpperCase() + descriptor.slice(1);
    return `${formattedDescriptor} ${category}`;
  }
  
  return null;
}

/**
 * Extracts a meaningful key phrase from the note using NLP-inspired techniques
 * @param note The note text
 * @returns An extracted key phrase or null if unable to extract
 */
function extractKeyPhrase(note: string): string | null {
  // Split into sentences and try to find the most informative one
  const sentences = note.split(/[.!?]+/).filter(s => s.trim().length > 0);
  
  // For very short notes, just use the whole thing with some cleanup
  if (note.length < 50) {
    return formatAsConcisePhrase(note);
  }
  
  // Try to find a sentence with key literary terms
  const literaryTerms = new Set([
    'metaphor', 'simile', 'imagery', 'symbol', 'alliteration', 'personification',
    'rhythm', 'rhyme', 'tone', 'theme', 'motif', 'contrast', 'irony'
  ]);
  
  for (const sentence of sentences) {
    const words = sentence.toLowerCase().split(/\s+/);
    for (const term of literaryTerms) {
      if (words.includes(term)) {
        // Found a sentence with a literary term, extract a phrase around it
        return extractPhraseAroundTerm(sentence, term);
      }
    }
  }
  
  // If no literary term found, use the first sentence as most informative
  if (sentences.length > 0) {
    return formatAsConcisePhrase(sentences[0]);
  }
  
  return null;
}

/**
 * Extracts a concise phrase around a specific term in a sentence
 * @param sentence The full sentence
 * @param term The term to locate in the sentence
 * @returns A concise phrase
 */
function extractPhraseAroundTerm(sentence: string, term: string): string {
  const words = sentence.split(/\s+/);
  const termIndex = words.findIndex(w => w.toLowerCase().includes(term));
  
  if (termIndex === -1) return formatAsConcisePhrase(sentence);
  
  // Get a few words before and after the term
  const startIndex = Math.max(0, termIndex - 2);
  const endIndex = Math.min(words.length, termIndex + 3);
  
  const phrase = words.slice(startIndex, endIndex).join(' ');
  return formatAsConcisePhrase(phrase);
}

/**
 * Formats text as a concise phrase suitable for a label
 * @param text The text to format
 * @returns A formatted concise phrase
 */
function formatAsConcisePhrase(text: string): string {
  // Remove unnecessary prefixes like "This is a" or "The poem uses"
  const cleanedText = text
    .replace(/^(this|the|there is a|this is a|the poem uses|the poem employs|the poet uses)/i, '')
    .trim();
  
  // Split into words and remove stopwords
  const words = cleanedText.split(/\s+/);
  const significantWords = words.filter(word => !STOPWORDS.has(word.toLowerCase()));
  
  // If we have significant words, use them; otherwise fall back to original words
  const usableWords = significantWords.length > 0 ? significantWords : words;
  
  // Take up to 3-4 words to keep it concise
  const phraseWords = usableWords.slice(0, 4);
  
  // If phrase is too short, try using more of the original text
  if (phraseWords.join(' ').length < 10 && words.length > 4) {
    return words.slice(0, 5).join(' ');
  }
  
  // Format the phrase with proper capitalization
  return phraseWords
    .map((word, index) => {
      // Capitalize first word and other major words, but not small connector words in the middle
      if (index === 0 || word.length > 3 || !STOPWORDS.has(word.toLowerCase())) {
        return word.charAt(0).toUpperCase() + word.slice(1);
      }
      return word;
    })
    .join(' ');
}

/**
 * Formats the first few words of a note into a suitable label
 * @param note The note text
 * @returns A formatted label from the first words
 */
function formatFirstWords(note: string): string {
  const words = note.split(/\s+/);
  
  // Take first 3-4 words but skip initial stopwords
  let startIndex = 0;
  while (startIndex < words.length && STOPWORDS.has(words[startIndex].toLowerCase())) {
    startIndex++;
  }
  
  // Take up to 4 meaningful words
  const endIndex = Math.min(startIndex + 4, words.length);
  const selectedWords = words.slice(startIndex, endIndex);
  
  // If we didn't get any useful words, just use the first 3-4 words
  if (selectedWords.length === 0) {
    return words.slice(0, 3).join(' ');
  }
  
  // Format with proper capitalization for a title
  return selectedWords
    .map((word, index) => {
      // Capitalize first word and other major words
      if (index === 0 || word.length > 3 || !STOPWORDS.has(word.toLowerCase())) {
        return word.charAt(0).toUpperCase() + word.slice(1);
      }
      return word;
    })
    .join(' ');
}

/**
 * Returns a default label based on the highlight metadata
 * Used as a last resort when no text content is available
 * @param highlight The highlight
 * @returns A default label
 */
function getDefaultLabel(highlight: PoemHighlight): string {
  if (highlight.is_style_specific) {
    return "Stylistic Element";
  }
  
  // For multi-line highlights
  if (highlight.start_line_index !== highlight.end_line_index) {
    return "Extended Literary Element";
  }
  
  return "Literary Device";
}

/**
 * Cache for generated labels to avoid regeneration
 */
const labelCache = new Map<string, string>();

/**
 * Generates a good display label for a highlight, with caching
 * @param highlight The highlight to generate a label for
 * @returns A content-driven label
 */
export function getDisplayLabel(highlight: PoemHighlight): string {
  // Add logging
  // console.log('[getDisplayLabel] Received highlight:', JSON.stringify(highlight, null, 2));

  // Check cache first
  if (labelCache.has(highlight.id)) {
    return labelCache.get(highlight.id)!;
  }
  
  const label = generateFallbackLabel(highlight);
  
  // Cache the result
  labelCache.set(highlight.id, label);
  
  return label;
} 